import {Directive, ElementRef, Inject, Input, OnDestroy, OnInit, Optional} from '@angular/core';
import {TableComponent} from '@blocks/table/table.component';
import {Point} from '@angular/cdk/drag-drop';
import {Subscription} from 'rxjs';
import {BLOCKS_CONFIG, BlocksConfig} from '@blocks/blocks.config';
import {CellSelection} from '@blocks/table/cell-selection';
import {RichTextService} from '@blocks/rich-text/rich-text.service';
import {BlockMutationService} from '@blocks/services/block-mutation/block-mutation.service';
import {TableService} from '@blocks/table/table.service';
import {BlockType, RichText} from '@paperlessio/sdk/api/models';

@Directive({
  selector: 'table[cellSelectable]',
  exportAs: 'cellSelectable'
})
export class TableCellSelectableDirective implements OnInit, OnDestroy {
  cellSelectMouseDown = false;
  cellSelectActive = false;
  cellSelectStartPosition: Point;
  cellSelectEndPosition: Point;

  @Input() cellSelectable: boolean;

  private subs = new Subscription();
  private referenceSelection: CellSelection;

  constructor(
    private elementRef: ElementRef,
    private tc: TableComponent,
    private tableService: TableService,
    @Optional() private blockMutationService: BlockMutationService,
    @Optional() private richTextService: RichTextService,
    @Optional() @Inject(BLOCKS_CONFIG) private blocksConfig: BlocksConfig) {
  }

  ngOnInit() {
    if (this.blocksConfig?.editable) {
      this.subs.add(this.tableService.mouseUp$.subscribe(event => this.handleMouseup(event)));
    }

    // this.subs.add(this.tableService.keyUp$
    //   .pipe(
    //     debounceTime(100),
    //     filter(event => !!event && this.tc.blockSelected && this.tc.cellSelection != null)
    //   )
    //   .subscribe(event => {
    //     this.handleKeyUp(event);
    //   }));
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  selectRow(index: number): void {
    const colCount = this.tableService.getColumnCount();

    this.tc.cellSelection = new CellSelection({
      x1: 0,
      x2: colCount - 1,
      y1: index,
      y2: index
    });

    this.referenceSelection = new CellSelection(this.tc.cellSelection);

    if (this.richTextService) {
      this.richTextService.blur();
    }

    this.tc.cdr.markForCheck();
  }

  selectColumn(index: number): void {
    const rowCount = this.tc.getRowCount();

    this.tc.cellSelection = new CellSelection({
      x1: index,
      x2: index,
      y1: 0,
      y2: rowCount - 1
    });

    this.referenceSelection = new CellSelection(this.tc.cellSelection);

    if (this.richTextService) {
      this.richTextService.blur();
    }

    this.tc.cdr.markForCheck();
  }

  selectCell(colIndex: number, rowIndex: number, cellId: number): void {
    this.cellSelectStartPosition = {
      x: colIndex,
      y: rowIndex
    };

    this.tc.cellSelection = new CellSelection({
      x1: colIndex,
      x2: colIndex,
      y1: rowIndex,
      y2: rowIndex
    });

    if (this.richTextService && this.tc.cellSelection && this.tc.cellSelection.width === 1 && this.tc.cellSelection.height === 1) {
      const foundBlocks = this.tc.children
        .map(row => row.children.find(cell => cell.id === cellId))
        .filter(row => !!row && row.children.length > 0);

      if (!!foundBlocks && foundBlocks.length > 0) {
        const foundBlock = foundBlocks[0];

        if (foundBlock.children.length === 1 && foundBlock.children[0].type === BlockType.RichText) {
          this.richTextService.focus(foundBlock.children[0].id);
        }
      }
    }

    this.referenceSelection = new CellSelection(this.tc.cellSelection);
    this.tc.cdr.markForCheck();
  }

  handleMouseup(event: MouseEvent): void {
    this.cellSelectStartPosition = null;
    this.cellSelectEndPosition = null;
    this.cellSelectActive = false;
    this.cellSelectMouseDown = false;
  }

  cellMouseClick(event: MouseEvent) {
    // this.tc.cellSelection = null;

    const dataset = (event.target as HTMLElement).closest('td').dataset;
    const colIndex = +dataset.colindex;
    const rowIndex = +dataset.rowindex;
    const cellId = +dataset.cellid;

    this.selectCell(colIndex, rowIndex, cellId);
  }

  cellMouseDown(event: MouseEvent): void {
    if (!this.tc.blockSelected || event.button !== 0) {
      return;
    }

    // When a dragMode is set, the user is currently dragging on an interactionElement and not a cell.
    if (this.tc.dragMode != null) {
      return;
    }

    const target = (event.target as HTMLElement).closest('td');

    this.cellSelectMouseDown = true;

    this.cellSelectStartPosition = {
      x: +(target as HTMLElement).dataset.colindex,
      y: +(target as HTMLElement).dataset.rowindex
    };
  }

  cellMouseLeave(event: MouseEvent): void {
    if (!this.tc.blockSelected || !this.cellSelectMouseDown) {
      return;
    }

    this.cellSelectActive = true;
  }

  cellMouseEnter(event: MouseEvent): void {
    if (!this.tc.blockSelected || !this.cellSelectMouseDown) {
      return;
    }

    this.cellSelectEndPosition = {
      x: +(event.target as HTMLElement).dataset.colindex,
      y: +(event.target as HTMLElement).dataset.rowindex
    };

    const posXs = [this.cellSelectStartPosition.x, this.cellSelectEndPosition.x].sort((a, b) => a - b);
    const posYs = [this.cellSelectStartPosition.y, this.cellSelectEndPosition.y].sort((a, b) => a - b);

    this.tc.cellSelection = new CellSelection({
      x1: posXs[0],
      x2: posXs[1],
      y1: posYs[0],
      y2: posYs[1]
    });

    // This causes performance-problems and we don't really need it.
    // if (this.richTextService) {
    //   this.richTextService.blur();
    // }

    this.referenceSelection = new CellSelection(this.tc.cellSelection);
  }

  private handleKeyUp(event: KeyboardEvent) {
    if (event.key === 'Backspace' && !this.tc.cellSelection.isSingleCellSelection) {
      this.emptySelectedCells();
    }

    if (event.shiftKey) {
      switch (event.key) {
        case 'ArrowUp': {
          this.tc.cellSelection = this.tc.cellSelection.expandSelection(this.referenceSelection, 'up');
          break;
        }
        case 'ArrowRight': {
          this.tc.cellSelection = this.tc.cellSelection.expandSelection(this.referenceSelection, 'right');
          break;
        }
        case 'ArrowDown': {
          this.tc.cellSelection = this.tc.cellSelection.expandSelection(this.referenceSelection, 'down');
          break;
        }
        case 'ArrowLeft': {
          this.tc.cellSelection = this.tc.cellSelection.expandSelection(this.referenceSelection, 'left');
          break;
        }
      }

      this.tc.cellSelection = this.tc.cellSelection
        .clamp(
          0,
          0,
          this.tableService.getColumnCount() - 1,
          this.tc.getRowCount() - 1
        );

      if (!this.tc.cellSelection?.isSingleCellSelection) {
        this.richTextService.blur();
      }
    } else {
      let newSelectionIndex = Number.MAX_SAFE_INTEGER;
      switch (event.key) {
        case 'ArrowUp': {
          this.tc.cellSelection = this.tc.cellSelection.moveSelection('up');
          newSelectionIndex = 0;
          break;
        }
        case 'ArrowRight': {
          this.tc.cellSelection = this.tc.cellSelection.moveSelection('right');
          break;
        }
        case 'ArrowDown': {
          this.tc.cellSelection = this.tc.cellSelection.moveSelection('down');
          break;
        }
        case 'ArrowLeft': {
          this.tc.cellSelection = this.tc.cellSelection.moveSelection('left');
          newSelectionIndex = 0;
          break;
        }
      }

      this.tc.cellSelection = this.tc.cellSelection
        .clamp(0,
          0,
          this.tableService.getColumnCount() - 1,
          this.tc.getRowCount() - 1
        );

      if (this.tc.cellSelection?.isSingleCellSelection) {
        const selectedCell = this.tc?.children[this.tc.cellSelection.y1]?.children[this.tc.cellSelection.x1];
        if (selectedCell?.children?.length > 0 && selectedCell.children[0].type === BlockType.RichText) {
          const rte = selectedCell.children[0] as RichText;
          this.richTextService.focus(rte.id);

          // TODO: Maybe set selection lige before, but reconsider it. Google doesn't do this either.
          // const foundKVPair = Array.from(instances).find(kv => kv[1].block.id === rte.id);
          // if (foundKVPair != null && foundKVPair.length === 2 && foundKVPair[0] != null && foundKVPair[1] != null) {
          //   foundKVPair[0].focus();
          //   foundKVPair[0].setSelection(newSelectionIndex, 0);
          //
          //   const selection = foundKVPair[0].getSelection();
          //   foundKVPair[1].previousSelection = selection;
          // }
        }
      }

      this.referenceSelection = new CellSelection(this.tc.cellSelection);
    }

    this.tc.cdr.markForCheck();
  }

  private emptySelectedCells() {
    this.blockMutationService.begin(this.tc.block.bucket_type, this.tc.block.bucket_id);

    const children = this.tc.cellSelection.getSelectedChildren(this.tc.block);
    children.forEach(row => row.forEach(cell => {
      cell.settings.backgroundColor = null;
      this.blockMutationService.addBlockUpdate(cell);

      // I grab the quill instance and set the Text to empty.
      // This should call blockMutationService.addBlockUpdate by itself so I don't have to that manually.
      const rte = cell.children[0];
      rte.settings.content = '';
      (rte.settings as any).delta = {
        ops: [
          {insert: '\n'}
        ]
      };

      this.blockMutationService.addBlockUpdate(rte);
    }));

    this.subs.add(this.blockMutationService.commit().subscribe());
  }
}






